import React, { type PropsWithChildren, memo, useMemo } from "react";
import clsx from "clsx";
import styles from "./Modal.module.scss";
import Button from "../atoms/Button";
import Icon from "../atoms/Icon";
import type { IconVariantTypes } from "../atoms/Icon.types";
import useModal from "../../../../../../shared-logic/hooks/useModal";

export type ModalProps = PropsWithChildren<{
    variant?:
        | "default"
        | "debug"
        | "fullscreen"
        | "fullscreen-spin"
        | "calculator"
        | "large"
        | "compare"
        | "filter"
        | "content-height"
        | "bottom"
        | "tradein";
    onRequestClose?: () => void;
    // Adds a title CTA on the top left of the modal that has an optional icon and a custom onClick function.
    headerAction?: { label: string; onClick: () => void; icon?: IconVariantTypes };
}> &
    React.HTMLProps<HTMLDialogElement>;

const Modal: React.FC<ModalProps> = ({
    open,
    variant = "default",
    children,
    onRequestClose,
    headerAction,
    className,
    ...rest
}) => {
    const dialogClasses = useMemo(() => {
        return clsx(styles.modal, styles[variant], className);
    }, [open, variant]);

    const { closeModal, ref } = useModal({ open: open ?? false, close: onRequestClose });

    return (
        <dialog ref={ref} {...rest} onCancel={closeModal} className={dialogClasses}>
            <div className={styles.wrapper}>
                <div className={styles.header}>
                    {headerAction && (
                        <Button className={styles["header-action"]} onClick={headerAction.onClick} variant="clean">
                            {headerAction.icon && (
                                <Icon className={styles.icon} sizing="md" variant={headerAction.icon} />
                            )}
                            {headerAction.label}
                        </Button>
                    )}

                    <Button
                        variant="clean"
                        sizing="sm"
                        onClick={closeModal}
                        aria-label="close"
                        className={styles.close}
                    >
                        <Icon variant="close-sm" sizing="sm" aria-hidden="true" />
                    </Button>
                </div>
                {children}
            </div>
        </dialog>
    );
};

export default memo(Modal);
